import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationBuilderAPI } from '../../api/applicationBuilderAPI';
import { loadSavedForm, saveForm, clearSavedForm, mergeSavedForm } from '../../utils/savedForms';
import './FormStyles.css';
import './CVFormStyles.css';

const makeEmptyEducation = () => ({
  school: '',
  city: '',
  state: '',
  country: '',
  degree_type: '',
  field: '',
  start: '',
  end: '',
  gpa: '',
  courses: ''
});

const makeEmptyExperience = () => ({
  company: '',
  city: '',
  state: '',
  country: '',
  title: '',
  group: '',
  start: '',
  end: '',
  summary: ''
});

const makeDefaultFormData = () => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  physical_address: '',
  education: [makeEmptyEducation()],
  experiences: [makeEmptyExperience()],
  languages_fluent: '',
  technical_skills: '',
  certifications: '',
  activities: '',
  interests: ''
});

const CVForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [restoredFromPrevious] = useState(() => Boolean(loadSavedForm('cv')));
  const [formData, setFormData] = useState(() =>
    mergeSavedForm(makeDefaultFormData(), loadSavedForm('cv'), ['education', 'experiences'])
  );

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await applicationBuilderAPI.getProfile();
        if (response.profile_data) {
          setProfileData(response.profile_data);
          // The last CV they built wins over profile auto-fill.
          if (restoredFromPrevious) return;
          setFormData(prev => ({
            ...prev,
            first_name: response.profile_data.first_name || '',
            last_name: response.profile_data.last_name || '',
            email: response.profile_data.email || '',
            phone: response.profile_data.phone || '',
            physical_address: response.profile_data.physical_address || '',
            education: response.profile_data.education || prev.education,
            experiences: response.profile_data.experiences || prev.experiences,
            languages_fluent: response.profile_data.languages_fluent || '',
            technical_skills: response.profile_data.technical_skills || '',
            certifications: response.profile_data.certifications || '',
            activities: response.profile_data.activities || ''
          }));
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    loadProfile();
  }, [restoredFromPrevious]);

  const startFresh = () => {
    clearSavedForm('cv');
    const blank = makeDefaultFormData();
    if (!profileData) {
      setFormData(blank);
      return;
    }
    // Match a first-time visit, which auto-fills from the profile.
    setFormData({
      ...blank,
      first_name: profileData.first_name || '',
      last_name: profileData.last_name || '',
      email: profileData.email || '',
      phone: profileData.phone || '',
      physical_address: profileData.physical_address || '',
      education: profileData.education || blank.education,
      experiences: profileData.experiences || blank.experiences,
      languages_fluent: profileData.languages_fluent || '',
      technical_skills: profileData.technical_skills || '',
      certifications: profileData.certifications || '',
      activities: profileData.activities || ''
    });
  };

  const handleInputChange = (section, index, field, value) => {
    if (section === 'education' || section === 'experiences') {
      const newArray = [...formData[section]];
      newArray[index] = { ...newArray[index], [field]: value };
      setFormData({ ...formData, [section]: newArray });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const addEducation = () => {
    setFormData({
      ...formData,
      education: [...formData.education, makeEmptyEducation()]
    });
  };

  const addExperience = () => {
    setFormData({
      ...formData,
      experiences: [...formData.experiences, makeEmptyExperience()]
    });
  };

  const removeEducation = (index) => {
    if (formData.education.length > 1) {
      setFormData({ ...formData, education: formData.education.filter((_, i) => i !== index) });
    }
  };

  const removeExperience = (index) => {
    if (formData.experiences.length > 1) {
      setFormData({ ...formData, experiences: formData.experiences.filter((_, i) => i !== index) });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await applicationBuilderAPI.generateDocument({
        document_type: 'cv',
        ...formData,
        use_ai: true
      });
      saveForm('cv', formData);
      navigate('/application-builder/editor/cv', { state: { documentData: response } });
    } catch (error) {
      console.error('Error generating CV:', error);
      alert(error?.data?.error === 'save_failed'
        ? error.data.message
        : 'Failed to generate CV. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="form-container cv-form-container">
      <div className="form-wrapper cv-form-wrapper">
        <div className="form-header cv-form-header">
          <h1>CV Builder</h1>
          {restoredFromPrevious && (
            <p className="builder-restored-note">
              Filled in from the last CV you built.
              <button type="button" className="builder-restored-reset" onClick={startFresh}>
                Start fresh
              </button>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="application-form cv-form">
          {/* Personal Details */}
          <section className="cv-section">
            <h2 className="cv-section-title">Personal Details</h2>
            <div className="cv-field-row">
              <div className="cv-field">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange(null, null, 'first_name', e.target.value)}
                  required
                />
              </div>
              <div className="cv-field">
                <label>Surname</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange(null, null, 'last_name', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="cv-field full-width">
              <label>Email address</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange(null, null, 'email', e.target.value)}
                required
              />
            </div>
            <div className="cv-field-row">
              <div className="cv-field">
                <label>Phone number</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange(null, null, 'phone', e.target.value)}
                />
              </div>
              <div className="cv-field">
                <label>Location</label>
                <input
                  type="text"
                  value={formData.physical_address}
                  onChange={(e) => handleInputChange(null, null, 'physical_address', e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Education */}
          <section className="cv-section">
            <h2 className="cv-section-title">Education</h2>
            {formData.education.map((edu, index) => (
              <div key={index} className="cv-education-block">
                {formData.education.length > 1 && (
                  <button type="button" className="remove-experience-btn" onClick={() => removeEducation(index)}>Remove</button>
                )}
                <div className="cv-field full-width">
                  <label>University / School</label>
                  <input
                    type="text"
                    value={edu.school}
                    onChange={(e) => handleInputChange('education', index, 'school', e.target.value)}
                  />
                </div>
                <div className="cv-field-row">
                  <div className="cv-field">
                    <label>Degree</label>
                    <input
                      type="text"
                      value={edu.degree_type}
                      onChange={(e) => handleInputChange('education', index, 'degree_type', e.target.value)}
                      placeholder="e.g. bachelor"
                    />
                  </div>
                  <div className="cv-field">
                    <label>Major</label>
                    <input
                      type="text"
                      value={edu.field}
                      onChange={(e) => handleInputChange('education', index, 'field', e.target.value)}
                    />
                  </div>
                </div>
                <div className="cv-field-row">
                  <div className="cv-field">
                    <label>Start Year</label>
                    <input
                      type="text"
                      value={edu.start}
                      onChange={(e) => handleInputChange('education', index, 'start', e.target.value)}
                    />
                  </div>
                  <div className="cv-field">
                    <label>End Year</label>
                    <input
                      type="text"
                      value={edu.end}
                      onChange={(e) => handleInputChange('education', index, 'end', e.target.value)}
                      placeholder="e.g. 2024"
                    />
                  </div>
                </div>
                <div className="cv-field-row">
                  <div className="cv-field">
                    <label>City</label>
                    <input
                      type="text"
                      value={edu.city}
                      onChange={(e) => handleInputChange('education', index, 'city', e.target.value)}
                    />
                  </div>
                  <div className="cv-field">
                    <label>Country</label>
                    <input
                      type="text"
                      value={edu.country}
                      onChange={(e) => handleInputChange('education', index, 'country', e.target.value)}
                    />
                  </div>
                </div>
                <div className="cv-field full-width">
                  <label>GPA/Grade (optional, if 3.5+ or equivalent)</label>
                  <input
                    type="text"
                    value={edu.gpa}
                    onChange={(e) => handleInputChange('education', index, 'gpa', e.target.value)}
                    placeholder="e.g. First Class"
                  />
                </div>
                <div className="cv-field full-width">
                  <label>Relevant Coursework (optional)</label>
                  <input
                    type="text"
                    value={edu.courses}
                    onChange={(e) => handleInputChange('education', index, 'courses', e.target.value)}
                    placeholder="e.g. Financial Modeling, Corporate Finance"
                  />
                </div>
              </div>
            ))}
            <button type="button" onClick={addEducation} className="add-button">+ Add education</button>
          </section>

          {/* Work Experience */}
          <section className="cv-section">
            <h2 className="cv-section-title">Work Experience</h2>
            <p className="cv-ai-hint">AI will create professional descriptions from your bullet points.</p>
            {formData.experiences.map((exp, index) => (
              <div key={index} className="cv-experience-card">
                <div className="cv-experience-card-title">Experience {index + 1}</div>
                <div className="cv-field-row">
                  <div className="cv-field">
                    <label>Company</label>
                    <input
                      type="text"
                      value={exp.company}
                      onChange={(e) => handleInputChange('experiences', index, 'company', e.target.value)}
                    />
                  </div>
                  <div className="cv-field">
                    <label>Job Title</label>
                    <input
                      type="text"
                      value={exp.title}
                      onChange={(e) => handleInputChange('experiences', index, 'title', e.target.value)}
                    />
                  </div>
                </div>
                <div className="cv-field-row">
                  <div className="cv-field">
                    <label>Group/Division</label>
                    <input
                      type="text"
                      value={exp.group}
                      onChange={(e) => handleInputChange('experiences', index, 'group', e.target.value)}
                    />
                  </div>
                  <div className="cv-field">
                    <label>State/Province</label>
                    <input
                      type="text"
                      value={exp.state}
                      onChange={(e) => handleInputChange('experiences', index, 'state', e.target.value)}
                      placeholder="NY, CA, Ontario, etc."
                    />
                  </div>
                </div>
                <div className="cv-field-row">
                  <div className="cv-field">
                    <label>City</label>
                    <input
                      type="text"
                      value={exp.city}
                      onChange={(e) => handleInputChange('experiences', index, 'city', e.target.value)}
                    />
                  </div>
                  <div className="cv-field">
                    <label>Country</label>
                    <input
                      type="text"
                      value={exp.country}
                      onChange={(e) => handleInputChange('experiences', index, 'country', e.target.value)}
                    />
                  </div>
                </div>
                <div className="cv-field-row">
                  <div className="cv-field">
                    <label>Start Date</label>
                    <input
                      type="text"
                      value={exp.start}
                      onChange={(e) => handleInputChange('experiences', index, 'start', e.target.value)}
                      placeholder="e.g. Sep 2025"
                    />
                  </div>
                  <div className="cv-field">
                    <label>End Date</label>
                    <input
                      type="text"
                      value={exp.end}
                      onChange={(e) => handleInputChange('experiences', index, 'end', e.target.value)}
                      placeholder="Present"
                    />
                  </div>
                </div>
                <div className="cv-field full-width">
                  <label>Key Achievements (each point on new line with &apos;-&apos;)</label>
                  <textarea
                    rows={4}
                    value={exp.summary}
                    onChange={(e) => handleInputChange('experiences', index, 'summary', e.target.value)}
                    placeholder="-Built a dcf and lbo model for a $1.2B sell-side M&A transaction."
                  />
                </div>
                {formData.experiences.length > 1 && (
                  <button type="button" className="remove-experience-btn" onClick={() => removeExperience(index)}>Remove experience</button>
                )}
              </div>
            ))}
            <button type="button" onClick={addExperience} className="cv-add-experience-box">
              Add experience
            </button>
          </section>

          {/* Skills & Activities */}
          <section className="cv-section">
            <h2 className="cv-section-title">Skills &amp; Activities</h2>
            <div className="cv-field full-width">
              <label>Languages</label>
              <input
                type="text"
                value={formData.languages_fluent}
                onChange={(e) => handleInputChange(null, null, 'languages_fluent', e.target.value)}
                placeholder="English, Spanish, French"
              />
              <p className="cv-field-hint">Languages will be displayed as &quot;English (Fluent), Spanish (Fluent)&quot;</p>
            </div>
            <div className="cv-field full-width">
              <label>Technical Skills</label>
              <input
                type="text"
                value={formData.technical_skills}
                onChange={(e) => handleInputChange(null, null, 'technical_skills', e.target.value)}
              />
            </div>
            <div className="cv-field full-width">
              <label>Certifications &amp; Training</label>
              <input
                type="text"
                value={formData.certifications}
                onChange={(e) => handleInputChange(null, null, 'certifications', e.target.value)}
              />
            </div>
            <div className="cv-field full-width">
              <label>Activities</label>
              <input
                type="text"
                value={formData.activities}
                onChange={(e) => handleInputChange(null, null, 'activities', e.target.value)}
                placeholder="Investment club, Student government, Volunteer work"
              />
            </div>
            <div className="cv-field full-width">
              <label>Interests</label>
              <input
                type="text"
                value={formData.interests}
                onChange={(e) => handleInputChange(null, null, 'interests', e.target.value)}
                placeholder="Marathon running, Chess, Photography"
              />
            </div>
          </section>

          <button type="submit" className="submit-button build-cv-btn" disabled={loading}>
            {loading ? 'Generating CV...' : 'Build CV'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CVForm;
